import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { pickBy } from 'lodash';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';

import { ModalCard } from 'partial/components/Modal';
import FormInput from 'partial/components/form/FormInput';
import FormSelect from 'partial/components/form/FormSelect';
import AsyncButton from 'modules/common/components/AsyncButton';
import { useRoles } from 'modules/access-control/roles-and-permissions/hooks';
import InputNumber from '../components/InputNumber';

import Password from '../components/Password';

import { useAddUser, useEditUser, useEditAdminProfile } from '../hooks';

const AddUpdateUser = ({ onClose, type, prevData }) => {
  const navigate = useNavigate();
  const { values, setFieldValue } = useFormik({
    initialValues: {
      role_id: prevData?.role?.id || null,
      first_name: prevData?.profile?.first_name || '',
      middle_name: prevData?.profile?.middle_name || '',
      last_name: prevData?.profile?.last_name || '',
      email: prevData?.email || '',
      mobile_number: prevData?.profile?.mobile_number || '',
      password: '',
      new_password_confirmation: '',
    },
  });

  const [, roles] = useRoles();
  const password = values?.password;
  const passwordConfirm = values?.new_password_confirmation;

  const testPassRegex = useMemo(() => {
    const pattern =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{10,}$/;
    return pattern?.test(password);
  }, [password]);

  const validatePass = useMemo(() => {
    const validate =
      !testPassRegex || !password?.length || !passwordConfirm?.length;
    return type === 'add' ? validate : false;
  }, [password, passwordConfirm, testPassRegex, type]);

  const mapRoles = useMemo(
    () =>
      roles?.map((item) => ({
        label: item?.name,
        value: item?.id,
      })),
    [roles],
  );

  const isFieldEmpty = useMemo(
    () =>
      !values?.role_id ||
      !values?.first_name?.length ||
      !values?.last_name?.length ||
      !values?.email?.length,
    [values],
  );

  const onEditUserSuccess = (res) => {
    onClose();
    navigate(`/access-control/user-management/${res?.data?.data?.uuid}`);
  };

  const [isEditLoading, editUser] = useEditUser(onEditUserSuccess);

  const onSuccess = (res) => {
    if (type === 'add') {
      onClose();
      navigate(
        `/access-control/user-management/${res?.data?.data?.user?.uuid}`,
      );
    } else editUser(pickBy({ id: prevData?.uuid, email: values?.email }));
  };

  const [isAddLoading, add] = useAddUser(onSuccess);
  const [isEditAdminLoading, editAdminProfile] = useEditAdminProfile(onSuccess);

  const isLoading = isAddLoading || isEditLoading || isEditAdminLoading;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (type === 'add')
      add(
        pickBy({
          ...values,
          password_confirmation: password,
          new_password_confirmation: '',
        }),
      );
    if (type === 'edit')
      editAdminProfile(
        pickBy({
          ...values,
          id: prevData?.uuid,
          email: '',
          new_password_confirmation: '',
        }),
      );
  };

  return (
    <ModalCard
      size="sm"
      title={`${type === 'add' ? 'ADD' : 'EDIT'} USER`}
      onClose={onClose}
      containerClassName="max-h-[95vh] overflow-auto"
    >
      <form className="px-4 py-6 " onSubmit={handleSubmit}>
        <div className="space-y-4">
          <FormSelect
            name="role_id"
            value={values?.role_id}
            onChange={setFieldValue}
            options={mapRoles}
            label="Role"
            placeHolder="Select Role"
            required
          />

          <FormInput
            name="first_name"
            onSetFieldValue={setFieldValue}
            value={values?.first_name}
            label="First Name"
            type="text"
            required
            placeHolder="Enter first name"
          />

          <FormInput
            name="middle_name"
            onSetFieldValue={setFieldValue}
            value={values?.middle_name}
            label="Middle Name (Optional)"
            type="text"
            placeHolder="Enter middle name"
          />

          <FormInput
            name="last_name"
            onSetFieldValue={setFieldValue}
            value={values?.last_name}
            label="Last Name"
            type="text"
            required
            placeHolder="Enter last name"
          />

          <FormInput
            name="email"
            onSetFieldValue={setFieldValue}
            value={values?.email}
            label="Email"
            type="email"
            required
            placeHolder="Enter email"
          />

          {type === 'add' && (
            <Password
              password={password}
              passwordConfirm={passwordConfirm}
              onChange={setFieldValue}
              testPassRegex={testPassRegex}
              passName="password"
              passOnly={false}
            />
          )}

          <InputNumber
            name="mobile_number"
            onSetFieldValue={setFieldValue}
            value={values?.mobile_number}
            label="Mobile Number"
            type="text"
            required
          />
        </div>

        <div className="flex justify-end gap-2 mt-10">
          <AsyncButton
            color="regular"
            className="font-[400] !text-gray-500"
            onClick={onClose}
          >
            Cancel
          </AsyncButton>
          <AsyncButton
            type="submit"
            className="font-[400]"
            color="regular"
            disabled={
              isFieldEmpty || validatePass || password !== passwordConfirm
            }
            isLoading={isLoading}
          >
            {type === 'add' ? 'add' : 'save'}
          </AsyncButton>
        </div>
      </form>
    </ModalCard>
  );
};

AddUpdateUser.defaultProps = {
  type: 'add',
  prevData: {},
};

AddUpdateUser.propTypes = {
  onClose: PropTypes.func.isRequired,
  type: PropTypes.string,
  prevData: PropTypes.instanceOf(Object),
};

export default AddUpdateUser;
